import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import { getStorefrontOrder } from '../../endpoints/chapterSupplies';
import { getCountries, getStates } from '../../endpoints/lookupsTs';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const StorefrontOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useApiGet(
    ['chapter-supplies-storefront-order', id],
    () => getStorefrontOrder(id!),
    { enabled: !!id }
  );
  const { data: countries = [] } = useApiGet(
    ['storefront-order-detail-countries'],
    getCountries
  );
  const { data: states = [] } = useApiGet(
    ['storefront-order-detail-states', data?.ship_to_country_id || ''],
    () => getStates(data?.ship_to_country_id || ''),
    { enabled: !!data?.ship_to_country_id }
  );

  const resolvedCountryName =
    (data?.ship_to_country_name && data.ship_to_country_name.trim()) ||
    countries.find((country) => country.id === data?.ship_to_country_id)
      ?.name ||
    'Unknown country';
  const resolvedStateName =
    (data?.ship_to_state_name && data.ship_to_state_name.trim()) ||
    states.find((state) => state.id === data?.ship_to_state_id)?.name ||
    'Unknown state/province';

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/chapter-supplies/storefront/orders')}
        sx={{ mb: 2 }}
      >
        Back to Orders
      </Button>
      {isLoading && <Typography>Loading order...</Typography>}
      {error && (
        <Typography color="error">Unable to load order details.</Typography>
      )}
      {data && (
        <Card>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 1 }}>
              {data.order_number != null
                ? `Order #${data.order_number}`
                : `Order ${data.order_id}`}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {new Date(data.placed_at).toLocaleString()} -{' '}
              {data.order_status_name || data.order_status_id}
            </Typography>

            <Typography variant="h6" sx={{ mb: 1 }}>
              Shipping
            </Typography>
            <Typography variant="body2">
              {data.ship_to_name}
              <br />
              {data.ship_to_address_line1}
              {data.ship_to_address_line2
                ? `, ${data.ship_to_address_line2}`
                : ''}
              <br />
              {data.ship_to_city}, {resolvedStateName}{' '}
              {data.ship_to_postal_code}
              <br />
              {resolvedCountryName}
            </Typography>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Items
            </Typography>
            <Stack spacing={1}>
              {data.lines.map((line, idx) => (
                <Box
                  key={
                    line.order_line_item_id ?? `${line.online_item_id}-${idx}`
                  }
                  sx={{ mb: 1 }}
                >
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">
                      {line.item_display_name?.trim() ||
                        line.item_name?.trim() ||
                        'Item'}{' '}
                      x {line.quantity}
                    </Typography>
                    <Typography variant="body2">
                      {formatPrice(line.subtotal)}
                    </Typography>
                  </Stack>
                  {line.custom_text_lines &&
                    line.custom_text_lines.length > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mt: 0.5, pl: 1 }}
                      >
                        {line.custom_text_lines.map((t, i) => (
                          <span key={i}>
                            {i > 0 ? ' · ' : ''}
                            {t}
                          </span>
                        ))}
                      </Typography>
                    )}
                  {line.personalization_display_name && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 0.5, pl: 1 }}
                    >
                      Member: {line.personalization_display_name}
                      {line.personalization_first_name
                        ? ` — Name on item: ${line.personalization_first_name}`
                        : ''}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography>Subtotal</Typography>
              <Typography>{formatPrice(data.subtotal)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>Freight</Typography>
              <Typography>{formatPrice(data.shipping_amount)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>Tax</Typography>
              <Typography>{formatPrice(data.tax)}</Typography>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography variant="h6">Total</Typography>
              <Typography variant="h6" color="primary.main">
                {formatPrice(data.total)}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default StorefrontOrderDetail;
